import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Building2,
    Users,
    AlertCircle,
    ArrowLeft
} from 'lucide-react';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Modal from '../../components/Common/Modal';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const DepartmentManagement = () => {
    const navigate = useNavigate();
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentDepartment, setCurrentDepartment] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: ''
    });

    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/departments');
            if (data.success) {
                setDepartments(data.data);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            toast.error('Failed to load departments');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (dept = null) => {
        if (dept) {
            setCurrentDepartment(dept);
            setFormData({
                name: dept.name,
                code: dept.code || '',
                description: dept.description || ''
            });
        } else {
            setCurrentDepartment(null);
            setFormData({
                name: '',
                code: '',
                description: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentDepartment(null);
        setFormData({ name: '', code: '', description: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (currentDepartment) {
                // Update
                const { data } = await api.put(`/departments/${currentDepartment._id}`, formData);
                if (data.success) {
                    toast.success('Department updated successfully');
                    setDepartments(departments.map(d => d._id === currentDepartment._id ? data.data : d));
                }
            } else {
                // Create
                const { data } = await api.post('/departments', formData);
                if (data.success) {
                    toast.success('Department created successfully');
                    setDepartments([...departments, data.data]);
                }
            }
            handleCloseModal();
        } catch (error) {
            console.error('Error saving department:', error);
            toast.error(error.response?.data?.message || 'Failed to save department');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this department?')) return;
        try {
            const { data } = await api.delete(`/departments/${id}`);
            if (data.success) {
                toast.success('Department deleted successfully');
                setDepartments(departments.filter(d => d._id !== id));
            }
        } catch (error) {
            console.error('Error deleting department:', error);
            toast.error('Failed to delete department');
        }
    };

    const filteredDepartments = departments.filter(dept =>
        dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dept.code?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredDepartments.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);

    return (
        <div className="space-y-6">

            {/* Header Section */}
            <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147]">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="space-y-0.5 text-left">
                        <h1 className="text-[22px] font-black text-[#1a3884] dark:text-[#4f7dff] tracking-tight">
                            Departments
                        </h1>
                        <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
                            <Building2 className="w-4 h-4 text-[#1a3884]/60 dark:text-[#4f7dff]/60" />
                            Manage academic departments
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-[#1a3884] hover:bg-[#132A55] active:scale-95 text-white rounded-xl shadow-md transition-all"
                >
                    <Plus className="w-4 h-4" />
                    Add Department
                </button>
            </div>

            <Card>
                <div className="mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search departments by name or code..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none transition-all"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-3"></div>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Loading departments...</p>
                    </div>
                ) : filteredDepartments.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 dark:bg-[#001f40]/20 rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                        <Building2 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-500 dark:text-gray-400 font-medium">No departments found</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Get started by adding a new department</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop View: Styled table for medium/large screens */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-800">
                                        <th className="py-4 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                                        <th className="py-4 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dept Code</th>
                                        <th className="py-4 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                                        <th className="py-4 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                    {currentItems.map((dept) => (
                                        <tr key={dept._id} className="hover:bg-gray-50 dark:hover:bg-[#001f40]/40 transition-colors">
                                            <td className="py-4 px-4">
                                                <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                                    <Building2 className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                                    <span>{dept.name}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-400 border border-blue-100/50 dark:border-blue-900/30">
                                                    {dept.code || '-'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                                {dept.description || '-'}
                                            </td>
                                            <td className="py-4 px-4 text-right space-x-2">
                                                <button
                                                    onClick={() => handleOpenModal(dept)}
                                                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-[#1a3884] dark:hover:text-[#4f7dff] transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(dept._id)}
                                                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile View: Dynamic Card List for smaller screens */}
                        <div className="grid grid-cols-1 gap-4 md:hidden">
                            {currentItems.map((dept) => (
                                <div
                                    key={dept._id}
                                    className="p-5 bg-white dark:bg-[#001f40]/40 rounded-xl border border-gray-100 dark:border-[#1a3884]/30 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                                >
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                                                <Building2 className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-snug">
                                                    {dept.name}
                                                </h3>
                                                <span className="inline-flex items-center mt-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-100/50 dark:border-indigo-900/30">
                                                    {dept.code || 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-3 mb-4 flex-grow">
                                        {dept.description || 'No description provided.'}
                                    </p>

                                    <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-50 dark:border-gray-800">
                                        <button
                                            onClick={() => handleOpenModal(dept)}
                                            className="p-2 text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 rounded-lg transition-colors flex items-center justify-center"
                                            title="Edit"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(dept._id)}
                                            className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors flex items-center justify-center"
                                            title="Delete"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination Footer */}
                        {totalPages > 1 && (
                            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 dark:border-gray-800 pt-4">
                                <div className="text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
                                    Showing <span className="font-semibold">{indexOfFirstItem + 1}</span> to <span className="font-semibold">{Math.min(indexOfLastItem, filteredDepartments.length)}</span> of <span className="font-semibold">{filteredDepartments.length}</span> results
                                </div>
                                <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-3">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="flex-1 sm:flex-none"
                                    >
                                        Previous
                                    </Button>
                                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="flex-1 sm:flex-none"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={currentDepartment ? 'Edit Department' : 'Add New Department'}
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label="Department Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="e.g. Computer Science Engineering"
                    />
                    <Input
                        label="Department Code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="e.g. CSE"
                    />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-[#4f7dff] focus:border-transparent bg-white dark:bg-[#001f40] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none h-24 transition-colors"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Brief description of the department..."
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button variant="outline" type="button" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {currentDepartment ? 'Update' : 'Create'}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default DepartmentManagement;
